import React, { useEffect, useState, useRef } from 'react';
import { Container } from 'semantic-ui-react';
import loadable from '@loadable/component';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { flattenToAppURL } from '@plone/volto/helpers';
import { flattenHTMLToAppURL } from '@plone/volto/helpers/Url/Url';
import Slider from 'react-slick';
import { ConditionalEmbed} from 'volto-gdpr-privacy';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './styleLuogo.css';

const MapComponent = loadable(() => import('./MapComponent'), {
  ssr: false,
  fallback: <div>Caricamento mappa...</div>,
});

// Configurazione del carosello
const carouselSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: true,
  arrows: true,
  autoplay: true,
  autoplaySpeed: 5000,
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hour = date.getHours();
  const minute = date.getMinutes();
  return isNaN(day) ? '' : `${day}-${month}-${year}, ${hour}:${minute}`;
};

// Estrae i link da un campo HTML
const extractLinksFromHTML = (html) => {
  if (!html) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const links = Array.from(doc.querySelectorAll('a[href]'));
  return links.map(link => ({
    url: link.getAttribute('href'),
    text: link.textContent || link.getAttribute('href')
  })).filter(link => link.url && !link.url.startsWith('#'));
};

const LuogoView = ({ content }) => {
  const lat = parseFloat(content.latitudine);
  const lng = parseFloat(content.longitudine);
  const hasValidCoordinates = !isNaN(lat) && !isNaN(lng);
  const coordinates = hasValidCoordinates ? [lat, lng] : [51.505, -0.09];

  const [activeSection, setActiveSection] = useState(null);
  const [accordionOpen, setAccordionOpen] = useState(true);
  const progressRef = useRef(null);

  const [attachments, setAttachments] = useState([]);
  const [images, setImages] = useState([]);
  const [relatedContents, setRelatedContents] = useState([]);
  const [videoContents, setVideoContents] = useState([]);
  const [loading, setLoading] = useState({
    attachments: false,
    images: false,
    relatedContents: false,
    videoContents: false
  });
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      setLoading({ attachments: true, images: true, relatedContents: true, videoContents: true });
      
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiPrefix = isLocalhost ? '' : '/api';

      const fetchFolderContents = async (folderName, portalTypes) => {
        const folderPath = `${location.pathname.replace(/\/$/, '')}/${folderName}/`;
        try {
          const types = Array.isArray(portalTypes) ? portalTypes : [portalTypes];
          const response = await axios.get(`${apiPrefix}/++api++${folderPath}@search`, {
            params: {
              portal_type: types,
              sort_on: 'getObjPositionInParent',
              b_size: 100,
              path: {
                query: folderPath,
                depth: 1,
              },
            },
          });

          const filteredItems = (response.data?.items || []).filter(item => {
            if (item['@type'] === 'Folder') {
              return false;
            }

            let itemPath = new URL(item['@id']).pathname;
            const normalizedItemPath = itemPath.replace('/api/', '/');
            const normalizedFolderPath = folderPath.replace('/api/', '/');

            const isDirectChild = normalizedItemPath.startsWith(normalizedFolderPath) &&
              normalizedItemPath !== normalizedFolderPath.replace(/\/$/, '') &&
              !normalizedItemPath.replace(normalizedFolderPath, '').includes('/');

            return isDirectChild;
          });

          if (types.includes('Link')) {
            const itemsWithLinkData = await Promise.all(
              filteredItems.map(async (item) => {
                if (item['@type'] === 'Link') {
                  try {
                    const path = new URL(item['@id']).pathname;

                    const pathVariants = [
                      path.replace(/^\/api/, ''),
                      path,
                      path.replace(/^\//, ''),
                    ];

                    let linkData = null;
                    let successPath = null;

                    for (const variant of pathVariants) {
                      try {
                        const testUrl = `${apiPrefix}/++api++${variant}`;
                        const response = await axios.get(testUrl);
                        linkData = response.data;
                        successPath = variant;
                        break;
                      } catch (err) {
                        console.log(`Fallito con path ${variant}:`, err.response?.status || err.message);
                        continue;
                      }
                    }

                    if (linkData) {
                      return {
                        ...item,
                        remoteUrl: linkData.remoteUrl || '',
                      };
                    } else {
                      console.error(`Impossibile caricare i dati per il link ${item.title}`);
                      return item;
                    }
                  } catch (error) {
                    console.error(`Errore generale nel caricamento del link ${item['@id']}:`, error);
                    return item;
                  }
                }
                return item;
              })
            );
            return itemsWithLinkData;
          }

          const itemsWithDownloadLinks = filteredItems.map(item => {
            if (item['@type'] === 'File') {
              return {
                ...item,
                '@id': item['@id'] + '/@@download/file',
              };
            }
            return item;
          });

          return itemsWithDownloadLinks;          
        } catch (error) {
          console.error(`Errore nel caricamento ${folderName}:`, error);
          return [];
        }
      };

      const [allegati, immagini, contenuticorrelati, video] = await Promise.all([
        fetchFolderContents('allegati', 'File'),
        fetchFolderContents('immagini', 'Image'),
        fetchFolderContents('contenuticorrelati', 'Document'),
        fetchFolderContents('video', 'Link'),
      ]);

      setAttachments(allegati);
      setImages(immagini);
      setRelatedContents(contenuticorrelati);
      setVideoContents(video);
      setLoading({ attachments: false, images: false, relatedContents: false, videoContents: false });
    };

    fetchData();
  }, [location.pathname, content.contenuti_correlati]);

  const hasAttachments = attachments.length > 0;
  const hasImages = images.length > 0;
  const hasRelatedContents = relatedContents.length > 0;
  const hasVideoLinks = videoContents.length > 0;

  const availableMenuItems = [
    { id: 'descrizione', label: 'Descrizione', show: !!content.descrizioneestesa?.data },
    { id: 'galleria-fotografica', label: 'Galleria Fotografica', show: hasImages },
    { id: 'video', label: 'Video', show: hasVideoLinks },
    { id: 'capacità-ricettiva', label: 'Capacità ricettiva', show: !!content.capacitaricettiva?.data },
    { id: 'età', label: 'Età', show: !!content.eta?.data },
    { id: 'caratteristiche', label: 'Caratteristiche', show: !!content.caratteristiche?.data },
    { id: 'modalità-di-accesso', label: 'Modalità di accesso', show: !!content.modalitaaccesso?.data },
    { id: 'indirizzo', label: 'Indirizzo', show: !!content.indirizzo },
    { id: 'orari', label: 'Orari', show: !!content.orari?.data },
    { id: 'contatti', label: 'Contatti', show: !!content.contatti?.data },
    { id: 'allegati', label: 'Allegati', show: hasAttachments },
    { id: 'contenuti-correlati', label: 'Contenuti correlati', show: hasRelatedContents },
  ].filter((item) => item.show);

  useEffect(() => {
    const sectionIds = availableMenuItems.map((item) => item.id);
    const sectionElements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.body.scrollHeight - window.innerHeight;
      const scrolled = (scrollTop / docHeight) * 100;

      if (progressRef.current) {
        progressRef.current.style.width = `${scrolled}%`;
        progressRef.current.setAttribute('aria-valuenow', scrolled.toFixed(2));
      }

      let current = null;
      for (let el of sectionElements) {
        const rect = el.getBoundingClientRect();
        if (rect.top >= 0 && rect.top < window.innerHeight / 2) {
          current = el.id;
          break;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [availableMenuItems]);

  const getVideoId = (url) => {
    if (!url) return null;
    
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (youtubeMatch) return { id: youtubeMatch[1], type: 'youtube' };
    
    const vimeoMatch = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
    if (vimeoMatch) return { id: vimeoMatch[1], type: 'vimeo' };
    
    return null;
  };

  return (
    <Container>
      <h1>{content.title}</h1>

      {content.description && (
        <section id="sottotitolo" style={{ marginBottom: '30px' }}>
          <p className="documentDescription">{content.description}</p>
        </section>
      )}

      {content.image?.download && (
        <div className="header-image-wrapper-luogo">
          <img
            className="header-image responsive"
            src={flattenToAppURL(content.image.download)}
            alt={content.title}
          />
        </div>
      )}

      <div className="luogo-layout-wrapper" style={{ marginTop: '20px' }}>
        {/* Indice - Primo elemento per mobile */}
        {availableMenuItems.length > 0 && (
          <div className="luogo-sidebar">
            <div className="sticky-wrapper navbar-wrapper page-side-menu">
              <nav className="navbar it-navscroll-wrapper navbar-expand-lg">
                <div className="menu-wrapper">
                  <div className="link-list-wrapper menu-link-list">
                    <div className="accordion-wrapper">
                      <div className="accordion">
                        <div className="accordion-header">
                          <button
                            onClick={() => setAccordionOpen(!accordionOpen)}
                            className="accordion-button"
                            aria-expanded={accordionOpen}
                            aria-controls="side-menu-body"
                            type="button"
                          >
                            Indice della pagina
                          </button>
                        </div>

                        <div className="mb-3">
                          <div className="progress">
                            <div className="progress-bar-label">
                              <span className="visually-hidden"></span>
                            </div>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              ref={progressRef}
                              aria-valuenow="0"
                              aria-valuemin="0"
                              aria-valuemax="100"
                              style={{ width: '0%' }}
                            ></div>
                          </div>
                        </div>

                        {accordionOpen && (
                          <div className="collapse show" id="side-menu-body">
                            <div className="accordion-body">
                              <ul className="link-list" data-element="page-index">
                                {availableMenuItems.map((item, index) => (
                                  <li className="nav-item" key={index}>
                                    <a
                                      className={`nav-link ${
                                        activeSection === item.id ? 'active' : ''
                                      }`}
                                      href={`#${item.id}`}
                                      id={`item-${item.id}`}
                                    >
                                      <span>{item.label}</span>
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            </div>
          </div>
        )}

        {/* Contenuto - Secondo elemento per mobile */}
        <div className="luogo-content">
          {content.descrizioneestesa?.data && (
            <section id="descrizione" style={{ marginBottom: '30px' }}>
              <h4>Descrizione</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.descrizioneestesa.data),
                }}
              />
            </section>
          )}

          {loading.images ? (
            <p>Caricamento immagini...</p>
          ) : hasImages && (
            <section id="galleria-fotografica" style={{ marginBottom: '30px' }}>
              <h4>Galleria Fotografica</h4>
              <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                <Slider {...carouselSettings}>
                  {images.map((image) => (
                    <div key={image['@id']} style={{ textAlign: 'center' }}>
                      <img
                        src={flattenToAppURL(image['@id'] + '/@@images/image/large')}
                        alt={image.title || 'Immagine'}
                        style={{ 
                          maxHeight: '500px',
                          maxWidth: '100%',
                          margin: '0 auto',
                          display: 'block'
                        }}
                      />
                      {image.description && (
                        <p style={{ marginTop: '10px' }}>{image.description}</p>
                      )}
                    </div>
                  ))}
                </Slider>
              </div>
            </section>
          )}

          {hasVideoLinks && (
            <section id="video" style={{ marginBottom: '30px' }}>
              <h4>Video</h4>
              <div className="block video align center video-container">
                {videoContents.map((video, index) => {
                  const videoInfo = video.remoteUrl ? getVideoId(video.remoteUrl) : null;
                  if (!videoInfo) {
                    console.warn(`Nessun URL video valido trovato per: ${video.title}`);
                    return null;
                  }

                  return (
                    <div className="video-inner" key={video['@id']}>
                      {videoInfo.type === 'youtube' ? (
                        <ConditionalEmbed url={`https://www.youtube.com/embed/${videoInfo.id}`}>
                        <iframe
                          width="100%"
                          height="100%"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none'
                          }}
                          src={`https://www.youtube.com/embed/${videoInfo.id}`}
                          title={video.title || `Video ${index + 1}`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                        </ConditionalEmbed>
                      ) : (
                        <iframe
                          width="100%"
                          height="100%"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none'
                          }}
                          src={`https://player.vimeo.com/video/${videoInfo.id}`}
                          title={video.title || `Video ${index + 1}`}
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {content.capacitaricettiva?.data && (
            <section id="capacità-ricettiva" style={{ marginBottom: '30px' }}>
              <h4>Capacità ricettiva</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.capacitaricettiva.data),
                }}
              />
            </section>
          )}

          {content.eta?.data && (
            <section id="età" style={{ marginBottom: '30px' }}>
              <h4>Età</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.eta.data),
                }}
              />
            </section>
          )}

          {content.caratteristiche?.data && (
            <section id="caratteristiche" style={{ marginBottom: '30px' }}>
              <h4>Caratteristiche</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.caratteristiche.data),
                }}
              />
            </section>
          )}

          {content.modalitaaccesso?.data && (
            <section id="modalità-di-accesso" style={{ marginBottom: '30px' }}>
              <h4>Modalità di accesso</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.modalitaaccesso.data),
                }}
              />
            </section>
          )}

          {content.indirizzo && (
            <section id="indirizzo" style={{ marginBottom: '30px' }}>
              <h4>Indirizzo</h4>
              <div className="card-wrapper" style={{ marginBottom: '20px' }}>
                <div className="card card-teaser border-left-card preview-image-card card-big-io-comune shadow mt-3 rounded mb-4 card">
                  <div className="card-body">
                    <div className="card-title">
                      <h3 className="h5 card-title">{content.title}</h3>
                    </div>
                    <div className="card-text">
                      <p>
                        {content.indirizzo}
                        {content.comune && ` - ${content.comune}`}
                        <br />
                        {content.cap}
                        {content.provincia && ` - ${content.provincia}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
             
              {hasValidCoordinates ? (
                <div style={{ height: '400px', marginBottom: '20px' }}>
                  <MapComponent
                    coordinates={coordinates}
                    address={[content.indirizzo, content.cap]
                      .filter(Boolean)
                      .join(', ')}
                  />
                </div>
              ) : (
                <p>Coordinate non valide o mancanti.</p>
              )}
            </section>
          )}

          {content.orari?.data && (
            <section id="orari" style={{ marginBottom: '30px' }}>
              <h4>Orari</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.orari.data),
                }}
              />
            </section>
          )}

          {content.contatti?.data && (
            <section id="contatti" style={{ marginBottom: '30px' }}>
              <h4>Contatti</h4>
              <div
                dangerouslySetInnerHTML={{
                  __html: flattenHTMLToAppURL(content.contatti.data),
                }}
              />
            </section>
          )}

          {loading.attachments ? (
            <p>Caricamento allegati...</p>
          ) : hasAttachments && (
            <section id="allegati" style={{ marginBottom: '30px' }}>
              <h4>Allegati</h4>
              <div className="simple-card-container">
                <div className="simple-card-listing"
                  style={{
                    gridTemplateColumns: `repeat(auto-fill, minmax(220px, 1fr))`
                  }}
                >
                  {attachments.map((item) => (
                    <div className="simple-card" key={item['@id']}>
                      <div className="card-content card-allegato">
                        <a
                          href={flattenToAppURL(item['@id'])}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="32"
                            fill="currentColor"
                            className="bi bi-link-45deg"
                            viewBox="0 0 24 24"
                          >
                            <path d="M11 22c-1.6.1-2.9-1.2-3-2.8V6c0-2.2 1.8-4 4-4s4 1.8 4 4v10h-1V6c0-1.7-1.3-3-3-3S9 4.3 9 6v13c0 1.1.9 2 2 2s2-.9 2-2V8c0-.6-.4-1-1-1s-1 .4-1 1v8h-1V8c0-1.1.9-2 2-2s2 .9 2 2v11c.1 1.6-1.2 2.9-2.8 3H11z"></path>
                          </svg>
                          {item.title || item.id}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {loading.relatedContents ? (
            <p>Caricamento contenuti correlati...</p>
          ) : hasRelatedContents && (
            <section id="contenuti-correlati" style={{ marginBottom: '30px' }}>
              <h4>Contenuti correlati</h4>
              <div className="simple-card-container">
                <div className="simple-card-listing"
                  style={{
                    gridTemplateColumns: `repeat(auto-fill, minmax(220px, 1fr))`
                  }}
                >
                  {relatedContents.map((item) => (
                    <div className="simple-card" key={item['@id']}>
                      <div className="card-content card-allegato">
                        <a
                          href={flattenToAppURL(item['@id'])}                          
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="32"
                            fill="currentColor"
                            className="bi bi-file-earmark"
                            viewBox="0 0 16 16"
                          >
                            <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5z" />
                          </svg>
                          {item.title || item.id}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          <div className="lastUpdate" style={{ marginTop: '40px' }}>
            <b>Ultimo aggiornamento:</b> {formatDate(content.modified)}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default LuogoView;